import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { ApiResponse } from '@/types';
import { API_BASE_URL } from '@/config/environment';
import { authStateManager } from './authStateManager';

function safeJsonStringify(value: unknown, maxLen = 8000): string {
  try {
    const s = JSON.stringify(value, null, 2);
    return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
  } catch {
    return String(value);
  }
}

/** Log backend rejection in the browser console (mirrors server-side debugging when terminal is quiet). */
function logBackendApiError(err: AxiosError): void {
  const cfg = err.config;
  const method = (cfg?.method || 'GET').toUpperCase();
  const base = cfg?.baseURL ?? '';
  const path = cfg?.url ?? '';
  const fullUrl = `${base}${path}` || path || '(unknown url)';
  const status = err.response?.status;
  const raw = err.response?.data;

  let body: unknown = raw;
  if (raw instanceof Blob) {
    body = '[Blob body — open Network tab to inspect]';
  } else if (typeof raw === 'string') {
    body = raw.slice(0, 2000) + (raw.length > 2000 ? '…' : '');
  }

  const summary =
    body &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    ('detail' in body || 'message' in body || 'error' in body || 'errors' in body)
      ? {
          detail: (body as { detail?: unknown }).detail,
          message: (body as { message?: unknown }).message,
          error: (body as { error?: unknown }).error,
          errors: (body as { errors?: unknown }).errors,
        }
      : body;

  // Two lines: first is grep-friendly; second is full JSON (DevTools often collapses objects in the first)
  console.error(`[API] ${method} ${fullUrl} -> ${status} ${err.response?.statusText || ''}`.trim());
  console.error('[API] response body (JSON):\n', safeJsonStringify(summary));
}

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        // Emit a global activity event so network usage counts as activity
        try {
          window.dispatchEvent(new Event('app-activity'));
        } catch {}
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // If data is FormData, remove Content-Type header so browser can set it with boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Any successful response indicates activity
        try {
          window.dispatchEvent(new Event('app-activity'));
        } catch {}
        return response;
      },
      async (error) => {
        // Error responses also indicate the user/app did something
        try {
          window.dispatchEvent(new Event('app-activity'));
        } catch {}
        const originalRequest = error.config;

        if (axios.isAxiosError(error) && error.response) {
          logBackendApiError(error);
        } else if (axios.isAxiosError(error) && !error.response) {
          console.error('[API] network / no response', {
            message: error.message,
            url: `${error.config?.baseURL ?? ''}${error.config?.url ?? ''}`,
            code: error.code,
          });
        }
        
        // Handle 401 errors (unauthorized) - but not for login requests
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/login/access-token')) {
          originalRequest._retry = true;

          const refreshed = await this.refreshToken();
          if (refreshed) {
            originalRequest.headers.Authorization = `Bearer ${this.getToken()}`;
            return this.client(originalRequest);
          }
          return Promise.reject(error);
        }

        return Promise.reject(error);
      }
    );
  }

  // Token management methods (matching React Native frontend)
  private getToken(): string | null {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;
    const normalized = token.trim();
    if (normalized === 'undefined' || normalized === 'null' || normalized === '') return null;
    return normalized;
  }

  private getRefreshToken(): string | null {
    const token = localStorage.getItem('refresh_token');
    if (!token) return null;
    const normalized = token.trim();
    if (normalized === 'undefined' || normalized === 'null' || normalized === '') return null;
    return normalized;
  }

  private setTokens(accessToken: string, refreshToken: string, refreshTokenExpiresAt: string): void {
    localStorage.setItem('auth_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('refresh_token_expires_at', refreshTokenExpiresAt);
  }

  private isRefreshTokenExpired(): boolean {
    const value = localStorage.getItem('refresh_token_expires_at');
    if (!value) return true;
    const expiresAt = new Date(value).getTime();
    if (Number.isNaN(expiresAt)) return true;
    const bufferMs = 5 * 60 * 1000; // refresh 5 minutes before actual expiry
    return Date.now() >= expiresAt - bufferMs;
  }

  /**
   * Refresh the access token. Deduped across concurrent callers (interceptor + token monitor)
   * so a rotating refresh token is never presented twice at once.
   * Only clears the session on a genuine auth rejection (401/403) from the refresh
   * endpoint — a network hiccup fails this attempt without logging the user out.
   */
  async refreshToken(): Promise<boolean> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }
    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh();
    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performRefresh(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearTokens();
      await authStateManager.handleTokenExpiration();
      return false;
    }

    if (this.isRefreshTokenExpired()) {
      this.clearTokens();
      await authStateManager.handleTokenExpiration();
      return false;
    }

    try {
      const response = await this.post('/login/refresh-token', { refresh_token: refreshToken });
      this.setTokens(response.access_token, response.refresh_token, response.refresh_token_expires_at);
      return true;
    } catch (refreshError) {
      const status = axios.isAxiosError(refreshError) ? refreshError.response?.status : undefined;
      const isAuthRejection = status === 401 || status === 403 || status === 400;
      if (isAuthRejection) {
        console.log('Refresh token rejected by server, logging out');
        this.clearTokens();
        await authStateManager.handleTokenExpiration();
      } else {
        console.warn('Token refresh failed due to network/server error, will retry later', refreshError);
      }
      return false;
    }
  }

  private clearTokens(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('refresh_token_expires_at');
    localStorage.removeItem('user_data');
  }

  // HTTP Methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete(url, config);
    return response.data;
  }

  // File upload method
  async upload<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    };

    const response = await this.client.post(url, formData, config);
    return response.data;
  }

  // Download method
  async download(url: string, filename?: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Get raw response for blob downloads
  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.client.get(url, {
      responseType: 'blob',
      ...config,
    });
    return response.data;
  }
}

export const apiClient = new ApiClient();
